import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  DateFormat,
  DatePickerInput,
  FAIconName,
  Icon,
  Text,
  Tooltip,
} from '@equip.health/ui';

import DateView from '~/components/maud/patients/treatmentEpisode/DateView';
import {
  EMPTY_PLACEHOLDER,
  TreatmentEpisodeProgramDetails,
} from '~/components/maud/patients/treatmentEpisode/Types';
import UniversalSelectTextView from '~/components/maud/patients/treatmentEpisode/UniversalSelectTextView';
import { InputColumn } from './InputColumn';
import DeleteRowIcon from './DeleteRowIcon';
import { formatProgramDates, toDate } from './util';
import { DateTime } from 'luxon';
import { EpisodeTreatmentProgram } from '~/lib/constants/patients.constants';
import { useMemo } from 'react';
export interface TreatmentEpisodeProgramProps {
  data: TreatmentEpisodeProgramDetails;
  index: number;
  isActiveStatus?: boolean;
  isLastProgram?: boolean;
  isReadOnly: boolean;
  treatmentProgramOptions: EpisodeTreatmentProgram[];
  onChange: (
    index: number,
    update: Partial<TreatmentEpisodeProgramDetails>,
  ) => void;
  onDelete: (index: number) => void;
}

const TreatmentEpisodeProgram = ({
  data,
  index,
  isActiveStatus,
  isLastProgram,
  isReadOnly,
  onDelete,
  onChange,
  treatmentProgramOptions,
}: TreatmentEpisodeProgramProps) => {
  const TOP_LEVEL_ID = `treatment-episode-program-${index}`;
  const TE_PROGRAM_START_DATE_ID = `${TOP_LEVEL_ID}__start-date`;
  const TE_PROGRAM_END_DATE_ID = `${TOP_LEVEL_ID}__end-date`;

  const handleChange = (field: string) => (value: string) =>
    onChange(index, { [field]: value });

  const startDate = toDate(data.startDate);
  const dtStr = useMemo(
    () =>
      startDate
        ? DateTime.fromJSDate(startDate).toFormat(DateFormat.longDate)
        : null,
    [startDate],
  );
  const toolTipText = useMemo(() => {
    if (index === 0) {
      return 'The start date for the first program must match the admit date.';
    }
    return 'The start date for a new program must immediately follow the end date of the previous program.';
  }, [index]);

  const shouldShowActionBar = useMemo(() => {
    return !isReadOnly && isLastProgram && data?.endDate && isActiveStatus;
  }, [isReadOnly, isLastProgram, data?.endDate, isActiveStatus]);

  return (
    <ChakraFlex direction="column" gap="12px" width="100%">
      <ChakraFlex
        alignItems="flex-end"
        flexDirection="row"
        gap="24px"
        width="100%"
      >
        <InputColumn
          id={TOP_LEVEL_ID}
          isRequired
          label={!isReadOnly ? 'Program' : ''}
        >
          <UniversalSelectTextView
            id={TOP_LEVEL_ID}
            isClearable={false}
            isReadOnly={isReadOnly || !isActiveStatus}
            onChange={handleChange('treatmentProgramName')}
            options={treatmentProgramOptions}
            value={data.treatmentProgramName}
          />
        </InputColumn>
        {isReadOnly ? (
          <InputColumn id={TE_PROGRAM_START_DATE_ID} isRequired label="">
            <ChakraBox>
              <Text color="neutral.secondary" variant="body">
                {formatProgramDates(data.startDate, data.endDate)}
              </Text>
            </ChakraBox>
          </InputColumn>
        ) : (
          <>
            <InputColumn
              id={TE_PROGRAM_START_DATE_ID}
              isRequired
              label="Start date"
              toolTip={
                <Tooltip
                  id={`${TOP_LEVEL_ID}__start-date-tooltip`}
                  placement="top-start"
                  tooltipText={toolTipText}
                >
                  <Icon
                    aria-label={FAIconName.infoCircleRegular}
                    color="neutral.secondary"
                    name={FAIconName.infoCircleRegular}
                    size="sm"
                  />
                </Tooltip>
              }
            >
              <ChakraBox flex="1 0 100%">
                {isReadOnly ? (
                  <Text
                    color={dtStr ? 'neutral.primary' : 'neutral.tertiary'}
                    id={TE_PROGRAM_START_DATE_ID}
                  >
                    {dtStr ?? EMPTY_PLACEHOLDER}
                  </Text>
                ) : (
                  <DatePickerInput
                    defaultDate={startDate}
                    id={TE_PROGRAM_START_DATE_ID}
                    isDisabled
                    maxDate={toDate(data.endDate)}
                    onSubmit={(value) =>
                      handleChange('startDate')(
                        DateTime.fromJSDate(value).toFormat(
                          DateFormat.shortYearMonthDay,
                        ),
                      )
                    }
                  />
                )}
              </ChakraBox>
            </InputColumn>
            <InputColumn
              id={TE_PROGRAM_END_DATE_ID}
              isRequired
              label="End date"
            >
              <DateView
                id={TE_PROGRAM_END_DATE_ID}
                isReadOnly={!isActiveStatus}
                minDate={DateTime.fromJSDate(startDate)
                  .plus({ days: 1 })
                  .toJSDate()}
                onChange={(value) => {
                  handleChange('endDate')(
                    value
                      ? DateTime.fromJSDate(value).toFormat(
                          DateFormat.shortYearMonthDay,
                        )
                      : null,
                  );
                }}
                value={toDate(data.endDate)}
              />
            </InputColumn>
          </>
        )}
        {!isReadOnly && isActiveStatus && (
          <DeleteRowIcon index={index} onClick={() => onDelete(index)} />
        )}
      </ChakraFlex>
      {shouldShowActionBar && (
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id="treatment-episode-program-warning"
          text="The last treatment program date must be null. Please remove it or add a program to continue."
          variant="critical"
        />
      )}
    </ChakraFlex>
  );
};

export default TreatmentEpisodeProgram;
